import { computed } from 'vue';
import { useSessionStore } from '@/store';
import type { CustomerVisiblePrice, Money, MoveJob, MoveMatch, PricingType } from '@/types';

/**
 * Resolves the customer-facing pricing fields from the booked job when one is
 * active, falling back to the selected match pre-booking.
 *
 * Selection is source-level, not per-field: once a job is active its values
 * win outright — a job whose label was cleared must not resurrect the match's
 * label, and a job with no visible lines must show none.
 */
export function useCustomerPricingSource() {
    const session = useSessionStore();

    // A declined or cancelled job can remain `current` while the customer
    // re-selects a match, so only a live job overrides the match.
    const activeJob = computed<MoveJob | null>(() => {
        const job = session.getJob as MoveJob | null;
        return job && !job.isCancelled && !job.isDeclined ? job : null;
    });

    const source = computed<MoveJob | MoveMatch | null>(
        () => activeJob.value ?? (session.getSelectedMatch as MoveMatch | null) ?? null,
    );

    const pricingType = computed<PricingType | null>(() => source.value?.pricing_type ?? null);

    const customerVisiblePrices = computed<CustomerVisiblePrice[]>(() => source.value?.customer_visible_prices ?? []);

    const otherFees = computed<Money | null>(() => source.value?.other_fees ?? null);

    const otherFeesLabel = computed<string>(() => source.value?.other_fees_label || 'Other fees');

    return {
        activeJob,
        source,
        pricingType,
        customerVisiblePrices,
        otherFees,
        otherFeesLabel,
    };
}
